import { TouchableOpacity, View } from 'react-native'
import styled, { css, useTheme } from 'styled-components'
import PropTypes from 'prop-types'
import { useButtonHandlers } from '@flix/common/elements/controls'
import Icon from '@flix/common/elements/images/Icon'
import Image from '@flix/common/elements/images/Image/Image'
import { H2, Paragraph } from '@flix/common/elements/typography'
import { useLayout } from '@flix/common/hooks'
import { media, useMediaIs } from '@flix/common/style/media'
import { InView } from '../../../../utils/inViewObserver/InView'
import { handleInViewChange } from '../../../ads/helpers/handleInViewChange.js'

const borderWidth = 'xs'

const TransparentContainer = styled(TouchableOpacity)`
  align-items: flex-end;
  justify-content: flex-start;
  flex-direction: ${(props) => (props.promoPosition === 'left' ? 'row' : 'row-reverse')};
  margin-top: ${(props) => props.theme.spacing.md}px;
`

// We allow `borderColor` to be set separately. If it is, and it is different from
// the background, then it is applied. Otherwise, a `transparent` border is used, which
// prevents semi-opaque colours compounding when they are the same. This way the background
// extends under the border and the result looks correct. This is useful for some workflows,
// e.g. 'selecting' the promo with a white border on Smart TV.
const TextAndIconWrapper = styled(View)`
  border: ${(props) => `${props.theme.spacing[borderWidth]}px solid`};
  border-color: ${(props) =>
    props.borderColor === props.bgColor || !props.borderColor ? 'transparent' : props.borderColor};
  background: ${(props) => props.bgColor};
  flex-direction: ${(props) => (props.promoPosition === 'left' ? 'row' : 'row-reverse')};

  ${(props) =>
    !props.image
      ? null
      : props.promoPosition === 'right'
      ? css`
          border-left-width: ${zeroIfImage};
        `
      : css`
          border-right-width: ${zeroIfImage};
        `}
`

const zeroIfImage = (props) => (props.image ? 0 : props.theme.spacing[borderWidth])

const TextContent = styled(View)`
  justify-content: center;
  padding: ${(props) => props.theme.spacing.sm}px;
  width: 150px;

  ${media.md`
    width: ${(props) => props.theme.spacing.lg * 6}px;
    padding: ${(props) => props.theme.spacing.sm * 1.5}px;
  `};
`

const Title = styled(H2)`
  font-size: ${(props) => props.fontSize}px;
  line-height: ${(props) => props.fontSize * 1.2}px;
  margin-bottom: ${(props) => props.theme.spacing.xsmall}px;
`

const Description = styled(Paragraph)`
  font-size: ${(props) => props.fontSize * 0.85}px;
  line-height: ${(props) => props.fontSize}px;
  color: ${(props) => props.color || props.theme.colors.grayLighte};
  margin: 0;
`

const IconWrapper = styled(View)`
  flex-grow: 0;
  align-self: stretch;
  justify-content: center;
  padding: 0 ${(props) => props.theme.spacing.sm}px;

  ${media.md`
    padding: ${(props) => props.theme.spacing.sm * 1.5}px;
  `};

  ${(props) => (props.promoPosition === 'right' ? 'padding-right: 0' : 'padding-left: 0')};
`

const ImageWrapper = styled(View)`
  border: ${(props) => `${props.theme.spacing[borderWidth]}px solid ${props.bgColor}`};
  flex-grow: 0;
  justify-content: center;
  background-color: ${(props) => props.bgColor};
  width: ${(props) => props.width}px;
  height: ${(props) => props.height}px;
  transform: ${(props) => `translateY(${props.theme.spacing.xs}px)`};
`

export const ConfigurablePromo = ({
  action,
  accessible,
  bgColor = 'rgba(0, 0, 0, 0.8)',
  borderColor,
  description,
  descriptionColor,
  festivalId,
  filmSlug,
  contentType,
  icon,
  image,
  position = 'left',
  title,
  titleColor,
  beacons,
  onRequestPause,
}) => {
  const theme = useTheme()

  const { handlers } = useButtonHandlers()
  const { onLayout, width: textContentWidth, height: textContentHeight } = useLayout()

  const atLeastTablet = useMediaIs('sm')

  const fontSize = Math.min(
    Math.round(textContentWidth * 0.08) || 12, // width is initially zero, needs a fallback
    theme.fontSizes[atLeastTablet ? 'large' : 'medium'],
  )

  const aspectRatio = image?.width && image?.height ? image.width / image.height : 16 / 9
  const imageHeight = textContentWidth * 0.45
  const imageWidth = imageHeight * aspectRatio

  const onPress = () => {
    if (action && handlers[action.handler]) {
      onRequestPause()
      handlers[action.handler]({ slug: filmSlug, type: contentType, festivalId, ...action })
    }
  }

  return (
    <InView onChange={handleInViewChange({ beacons })} threshold={0.5}>
      {!!title && (
        <TransparentContainer
          // Named `promoPosition` to avoid conflict with `position` attribute
          promoPosition={position}
          onPress={onPress}
          as={accessible === false ? View : undefined}
        >
          <TextAndIconWrapper
            bgColor={bgColor}
            borderColor={borderColor}
            promoPosition={position}
            image={image}
          >
            <TextContent onLayout={onLayout} promoPosition={position}>
              <Title numberOfLines={1} color={titleColor} fontSize={fontSize}>
                {title}
              </Title>
              <Description numberOfLines={2} color={descriptionColor} fontSize={fontSize}>
                {description}
              </Description>
            </TextContent>
            {icon && !image && (
              <IconWrapper promoPosition={position} height={textContentHeight}>
                <Icon name={icon} size={Math.round(textContentWidth * 0.2)} />
              </IconWrapper>
            )}
          </TextAndIconWrapper>

          {image && !!imageWidth && (
            <ImageWrapper
              height={imageHeight + theme.spacing[borderWidth] * 2}
              width={imageWidth + theme.spacing[borderWidth] * 2}
              bgColor={borderColor || bgColor}
            >
              <Image height={imageHeight} width={imageWidth} source={{ uri: image.url }} />
            </ImageWrapper>
          )}
        </TransparentContainer>
      )}
    </InView>
  )
}

ConfigurablePromo.propTypes = {
  action: PropTypes.shape({
    handler: PropTypes.string.isRequired,
    type: PropTypes.string,
    slug: PropTypes.string,
    url: PropTypes.string,
  }),
  bgColor: PropTypes.string,
  borderColor: PropTypes.string,
  description: PropTypes.string.isRequired,
  descriptionColor: PropTypes.string,
  festivalId: PropTypes.string,
  filmSlug: PropTypes.string,
  icon: PropTypes.string,
  image: PropTypes.shape({
    url: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  position: PropTypes.oneOf(['right', 'left']).isRequired,
  title: PropTypes.string.isRequired,
  onRequestPause: PropTypes.func.isRequired,
}
